
// Debug mode - set to true to show shutdown debug lists above heatmaps
const SHOW_HEATMAP_DEBUG = false;

import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import {
    getMaintenanceSummary,
    getMtbfMttrByWorkplace,
    getSeverityDistribution,
    MtbfMttrByWorkplaceItem,
    getAggregatedMaintenanceData,
    AggregatedMaintenanceItem,
    AggregationDimension,
    getShutdownHeatmap,
    ShutdownHeatmapResult,
    getFailureHeatmap,
    FailureHeatmapResult,
    getPlannedShutdownStartsHeatmap,
    getPlannedShutdownDurationHeatmap,
    getLongestShutdowns,
    LongestShutdownItem,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { GlobalVariables } from '@/libs/core/global';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import {
    getFilterById,
    unpackSettings,
    getSelectedFilterLocal,
    setSelectedFilterLocal,
} from '@/libs/tpm-analytics/core/saved-filters';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsMaintenance extends Page {
    // Debug flag
    showHeatmapDebug = SHOW_HEATMAP_DEBUG;

    mtbfHours: number | null = null;
    mttrHours: number | null = null;
    failuresCount = 0;
    criticalCount = 0;
    repairCost = 0;
    byWorkplace: MtbfMttrByWorkplaceItem[] = [];
    severityLabels: string[] = [];
    severityValues: number[] = [];
    severityColors: string[] = [];
    aggregatedData: AggregatedMaintenanceItem[] = [];
    selectedDimensions: AggregationDimension[] = ['defect'];
    sortBy: 'count' | 'duration' = 'count';
    aggregatedMaintenanceTop: 10 | 25 | 50 = 10;
    // longest shutdowns
    longestShutdowns: LongestShutdownItem[] = [];
    longestShutdownsTop: 10 | 25 | 50 = 10;
    showImmediateMeasures = false;
    // heatmap state
    heatGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    totalShutdowns = 0;
    avgPerDay = 0;
    criticalDays: number[] = [];
    timeFrom = 8;
    timeTo = 16;
    palette = ['#e6f7ef', '#c8f1d3', '#fde68a', '#f59e0b', '#ef4444'];
    // failures heatmap
    failureGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    failureTotalCount = 0;
    failureAvgPerDay = 0;
    failureCriticalDays: number[] = [];
    failureTopHours: number[] = [];
    failureDebugItems: Array<{ from: string; fromLocal: string }> = [];
    // shutdown duration heatmap
    heatDebugItems: Array<{ from: string; to: string; fromLocal: string; toLocal: string }> = [];
    // planned shutdowns heatmaps
    plannedStartsGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    plannedStartsTotalCount = 0;
    plannedStartsAvgPerDay = 0;
    plannedStartsCriticalDays: number[] = [];
    plannedStartsTopHours: number[] = [];
    plannedStartsDebugItems: Array<{ from: string; fromLocal: string }> = [];
    plannedDurationGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    plannedDurationTotalCount = 0;
    plannedDurationAvgPerDay = 0;
    plannedDurationCriticalDays: number[] = [];
    plannedDurationTimeFrom = 8;
    plannedDurationTimeTo = 16;
    plannedDurationDebugItems: Array<{ from: string; to: string; fromLocal: string; toLocal: string }> = [];

    // Filters
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];
    maintenanceGranularity: 'day' | 'week' | 'month' = 'day';
    applyingInitial = false;
    filtersDebounceTimer: any = null;

    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    mounted() {
        // V mounted() VŽDY voláme load() - created() pouze nastavil stav
        this.load();

        // Watch registrujeme až v mounted(), aby se nespustil při async načítání initial state v created()
        this.$watch('maintenanceGranularity', () => {
            this.onFiltersChanged();
        });

        this.$watch('selectedDimensions', () => {
            // Uložit do localStorage
            try {
                localStorage.setItem('tpm-aggregation-dimensions', JSON.stringify(this.selectedDimensions));
            } catch (e) {
                console.warn('Nepodařilo se uložit dimenze do localStorage', e);
            }
            
            // Při změně dimenzí znovu načíst data
            if (this.selectedDimensions.length > 0) {
                this.onFiltersChanged();
            }
        });

        this.$watch('sortBy', () => {
            // Uložit do localStorage
            try {
                localStorage.setItem('tpm-aggregation-sortby', this.sortBy);
            } catch (e) {
                console.warn('Nepodařilo se uložit sortBy do localStorage', e);
            }
            
            // Při změně řazení není potřeba načítat data znovu, jen se změní řazení a TreeMap
            this.$forceUpdate();
        });
    }

    get periodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { text: this.$t('tpmFilters.last3m') as any, value: 'last3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }

    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.maintenanceGranularity,
        });
    }

    async load() {
        if (!this.tenantId) return;
        const { from, to } = this.getRange();
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any;
        const summary = await getMaintenanceSummary(from, to, filters);
        this.mtbfHours = summary.mtbfHours;
        this.mttrHours = summary.mttrHours;
        this.failuresCount = summary.failuresCount;
        this.criticalCount = summary.criticalCount;
        this.repairCost = summary.repairCost;

        this.byWorkplace = await getMtbfMttrByWorkplace(from, to, filters);
        const dist = await getSeverityDistribution(from, to, filters);
        this.severityLabels = dist.map((d) => d.label);
        this.severityValues = dist.map((d) => d.count);
        this.severityColors = dist.map((d) => d.color || '#94a3b8');

        this.aggregatedData = await getAggregatedMaintenanceData(from, to, this.selectedDimensions, 9999, filters);
        this.longestShutdowns = await getLongestShutdowns(from, to, this.longestShutdownsTop, filters);
        const heat: ShutdownHeatmapResult = await getShutdownHeatmap(from, to, filters);
        this.heatGrid = heat.grid;
        this.totalShutdowns = heat.totalCount;
        this.avgPerDay = heat.avgPerDay;
        this.criticalDays = heat.criticalDayIndexes;
        this.timeFrom = heat.commonRangeFromHour;
        this.timeTo = heat.commonRangeToHour;
        this.heatDebugItems = heat.debugItems || [];

        const fh: FailureHeatmapResult = await getFailureHeatmap(from, to, filters);
        this.failureGrid = fh.grid;
        this.failureTotalCount = fh.totalFailures;
        this.failureAvgPerDay = fh.avgPerDay;
        this.failureCriticalDays = fh.criticalDayIndexes;
        this.failureTopHours = fh.topHours;
        this.failureDebugItems = fh.debugItems || [];

        const plannedStarts: FailureHeatmapResult = await getPlannedShutdownStartsHeatmap(from, to, filters);
        this.plannedStartsGrid = plannedStarts.grid;
        this.plannedStartsTotalCount = plannedStarts.totalFailures;
        this.plannedStartsAvgPerDay = plannedStarts.avgPerDay;
        this.plannedStartsCriticalDays = plannedStarts.criticalDayIndexes;
        this.plannedStartsTopHours = plannedStarts.topHours;
        this.plannedStartsDebugItems = plannedStarts.debugItems || [];

        const plannedDuration: ShutdownHeatmapResult = await getPlannedShutdownDurationHeatmap(from, to, filters);
        this.plannedDurationGrid = plannedDuration.grid;
        this.plannedDurationTotalCount = plannedDuration.totalCount;
        this.plannedDurationAvgPerDay = plannedDuration.avgPerDay;
        this.plannedDurationCriticalDays = plannedDuration.criticalDayIndexes;
        this.plannedDurationDebugItems = plannedDuration.debugItems || [];
        this.plannedDurationTimeFrom = plannedDuration.commonRangeFromHour;
        this.plannedDurationTimeTo = plannedDuration.commonRangeToHour;
    }

    async onLongestShutdownsTopChanged(newTop: 10 | 25 | 50) {
        this.longestShutdownsTop = newTop;
        // Uložení do localStorage
        try {
            localStorage.setItem('tpm-longest-shutdowns-top', String(newTop));
        } catch (e) {
            console.warn('Nepodařilo se uložit nastavení do localStorage', e);
        }
        const { from, to } = this.getRange();
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any;
        this.longestShutdowns = await getLongestShutdowns(from, to, this.longestShutdownsTop, filters);
    }

    onShowImmediateMeasuresChanged() {
        // Uložení do localStorage
        try {
            localStorage.setItem('tpm-show-immediate-measures', String(this.showImmediateMeasures));
        } catch (e) {
            console.warn('Nepodařilo se uložit nastavení do localStorage', e);
        }
    }

    onAggregatedMaintenanceTopChanged(newTop: 10 | 25 | 50) {
        this.aggregatedMaintenanceTop = newTop;
        // Uložení do localStorage
        try {
            localStorage.setItem('tpm-aggregated-maintenance-top', String(newTop));
        } catch (e) {
            console.warn('Nepodařilo se uložit nastavení do localStorage', e);
        }
    }

    onFiltersChanged() {
        if (this.applyingInitial) return;
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            this.load();
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
        }, 200);
    }

    // auto-reload on period changes (like OEE)
    $watch!: any;
    created() {
        // Register callbacks for parent (TpmAnalytics.vue)
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        
        // Načtení nastavení z localStorage (volá se vždy jako výchozí)
        this.loadLongestShutdownsSettings();
        this.loadAggregationSettings();
        
        // Prefer query -> sid -> local
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
            return;
        }
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
        }

        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
    }

    loadLongestShutdownsSettings() {
        try {
            const savedTop = localStorage.getItem('tpm-longest-shutdowns-top');
            if (savedTop && ['10', '25', '50'].includes(savedTop)) {
                this.longestShutdownsTop = parseInt(savedTop, 10) as 10 | 25 | 50;
            }

            const savedShowMeasures = localStorage.getItem('tpm-show-immediate-measures');
            if (savedShowMeasures !== null) {
                this.showImmediateMeasures = savedShowMeasures === 'true';
            }

            const savedAggregatedTop = localStorage.getItem('tpm-aggregated-maintenance-top');
            if (savedAggregatedTop && ['10', '25', '50'].includes(savedAggregatedTop)) {
                this.aggregatedMaintenanceTop = parseInt(savedAggregatedTop, 10) as 10 | 25 | 50;
            }
        } catch (e) {
            console.warn('Nepodařilo se načíst nastavení z localStorage', e);
        }
    }

    loadAggregationSettings() {
        try {
            // Načíst selectedDimensions
            const savedDimensions = localStorage.getItem('tpm-aggregation-dimensions');
            if (savedDimensions) {
                try {
                    const dimensions = JSON.parse(savedDimensions) as AggregationDimension[];
                    if (Array.isArray(dimensions) && dimensions.length > 0) {
                        // Ověřit, že všechny hodnoty jsou platné dimenze
                        const validDimensions: AggregationDimension[] = ['location', 'workplace', 'station', 'machineComponent', 'shutdownType', 'defect'];
                        const filtered = dimensions.filter(d => validDimensions.includes(d));
                        if (filtered.length > 0) {
                            this.selectedDimensions = filtered;
                        }
                    }
                } catch (parseError) {
                    console.warn('Chyba při parsování dimenzí z localStorage', parseError);
                }
            }

            // Načíst sortBy
            const savedSortBy = localStorage.getItem('tpm-aggregation-sortby');
            if (savedSortBy && (savedSortBy === 'count' || savedSortBy === 'duration')) {
                this.sortBy = savedSortBy;
            }
        } catch (e) {
            console.warn('Nepodařilo se načíst nastavení agregace z localStorage', e);
        }
    }

    destroyed() {
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState)
            delete (this.$root as any).$tpmGetCurrentState;
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) delete (this.$root as any).$tpmApplyState;
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.maintenanceGranularity,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        this.applyingInitial = true;
        try {
            if (state?.periodPreset) this.periodPreset = state.periodPreset;
            if (state?.customFrom) this.customFrom = state.customFrom;
            if (state?.customTo) this.customTo = state.customTo;
            this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
            this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
            this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
            this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
            this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds)
                ? state.selectedShiftTeamTypeIds
                : [];
            if (state?.granularity) {
                this.maintenanceGranularity = state.granularity;
            } else if (state?.maintenanceGranularity) {
                // Zpětná kompatibilita se starými uloženými filtry
                this.maintenanceGranularity = state.maintenanceGranularity;
            }
        } finally {
            this.applyingInitial = false;
        }
        if (reload) {
            this.onFiltersChanged();
        }
    }

    get currentPeriodText() {
        const { from, to } = this.getRange();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(from)} – ${fmt(to)}`;
    }
    get previousPeriodText() {
        const { from, to } = this.getRange();
        const spanMin = Math.max(1, moment.tz(to, DISPLAY_TIMEZONE).diff(moment.tz(from, DISPLAY_TIMEZONE), 'minutes'));
        // Předchozí období končí tam, kde začíná aktuální (zobrazení jako exkluzivní konec)
        const prevTo = from;
        const prevFrom = moment.tz(from, DISPLAY_TIMEZONE).subtract(spanMin, 'minutes').toISOString();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('ddd DD.MM.YYYY HH:mm');
        return `${fmt(prevFrom)} – ${fmt(prevTo)}`;
    }

    formatHours(v: number | null): string {
        if (v == null || isNaN(v as any)) return '-';
        return v.toFixed(1) + 'h';
    }
    get currencyCode(): string {
        try {
            const tenants = tenantsStore.useGetter(CrudGetter.Data) as any[];
            const tenantId = this.tenantId as string;
            const t = tenants?.first
                ? tenants.first((x: any) => x.id === tenantId)
                : (tenants || []).find((x) => x.id === tenantId);
            const code = (t?.currency || '').toString().trim().toUpperCase();
            return code || 'CZK';
        } catch {
            return 'CZK';
        }
    }
    currencyLocale(): string {
        const code = this.currencyCode;
        switch (code) {
            case 'CZK':
                return 'cs-CZ';
            case 'EUR':
                return 'de-DE';
            case 'USD':
                return 'en-US';
            case 'GBP':
                return 'en-GB';
            case 'JPY':
                return 'ja-JP';
            case 'CNY':
                return 'zh-CN';
            case 'CHF':
                return 'de-CH';
            case 'AUD':
                return 'en-AU';
            case 'CAD':
                return 'en-CA';
            case 'SEK':
                return 'sv-SE';
            case 'NOK':
                return 'nb-NO';
            case 'DKK':
                return 'da-DK';
            case 'PLN':
                return 'pl-PL';
            case 'HUF':
                return 'hu-HU';
            case 'INR':
                return 'hi-IN';
            case 'KRW':
                return 'ko-KR';
            case 'HKD':
                return 'zh-HK';
            case 'SGD':
                return 'en-SG';
            case 'NZD':
                return 'en-NZ';
            case 'ZAR':
                return 'en-ZA';
            case 'TRY':
                return 'tr-TR';
            case 'RUB':
                return 'ru-RU';
            case 'AED':
                return 'ar-AE';
            case 'SAR':
                return 'ar-SA';
            case 'ILS':
                return 'he-IL';
            case 'THB':
                return 'th-TH';
            case 'MYR':
                return 'ms-MY';
            case 'IDR':
                return 'id-ID';
            case 'TWD':
                return 'zh-TW';
            case 'VND':
                return 'vi-VN';
            case 'MXN':
                return 'es-MX';
            case 'BRL':
                return 'pt-BR';
            case 'ARS':
                return 'es-AR';
            case 'COP':
                return 'es-CO';
            default:
                return GlobalVariables.lang || 'cs';
        }
    }
    get currencySymbol(): string {
        try {
            const parts = new Intl.NumberFormat(this.currencyLocale(), {
                style: 'currency',
                currency: this.currencyCode,
                currencyDisplay: 'narrowSymbol',
            }).formatToParts(1);
            return parts.find((p) => p.type === 'currency')?.value || this.currencyCode;
        } catch {
            return this.currencyCode;
        }
    }
    formatCost(v: number | null): string {
        const num = Math.round(Number(v || 0));
        try {
            return new Intl.NumberFormat(this.currencyLocale(), {
                style: 'currency',
                currency: this.currencyCode,
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(num);
        } catch {
            return `${new Intl.NumberFormat(GlobalVariables.lang || 'cs').format(num)} ${this.currencyCode}`;
        }
    }

    get longestShutdownsHeaders() {
        const headers = [
            { text: this.$t('tpmAnalytics.longestShutdownsWorkplace'), value: 'workplaceName', align: 'left' },
            { text: this.$t('tpmAnalytics.longestShutdownsFrom'), value: 'shutdownFrom', align: 'left' },
            { text: this.$t('tpmAnalytics.longestShutdownsTo'), value: 'shutdownTo', align: 'left' },
            { text: this.$t('tpmAnalytics.longestShutdownsDuration'), value: 'durationMinutes', align: 'left' },
            { text: this.$t('tpmAnalytics.longestShutdownsReason'), value: 'reason', align: 'left' },
        ];
        if (this.showImmediateMeasures) {
            headers.push({ text: this.$t('tpmAnalytics.longestShutdownsMeasures'), value: 'immediateMeasures', align: 'left' });
        }
        return headers;
    }

    formatDateTime(isoString: string): string {
        if (!isoString) return '—';
        return moment(isoString).local().format('DD.MM.YYYY HH:mm');
    }

    formatDuration(minutes: number): string {
        if (!minutes || minutes < 0) return '—';
        return `${Math.round(minutes)} min`;
    }

    get aggregatedHeaders() {
        const headers: any[] = [];

        // Definovat pořadí dimenzí (stejné jako v toggle)
        const dimensionOrder: AggregationDimension[] = [
            'location',
            'workplace',
            'station',
            'machineComponent',
            'shutdownType',
            'defect',
        ];

        // Přidat sloupce podle pořadí dimenzí, ale pouze pro vybrané
        for (const dim of dimensionOrder) {
            if (!this.selectedDimensions.includes(dim)) continue;

            switch (dim) {
                case 'location':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionLocation'), value: 'locationName', align: 'left' });
                    break;
                case 'workplace':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionWorkplace'), value: 'workplaceName', align: 'left' });
                    break;
                case 'station':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionStation'), value: 'stationName', align: 'left' });
                    break;
                case 'machineComponent':
                    headers.push({
                        text: this.$t('tpmAnalytics.dimensionMachineComponent'),
                        value: 'machineComponentDisplay',
                        align: 'left',
                    });
                    break;
                case 'shutdownType':
                    headers.push({
                        text: this.$t('tpmAnalytics.dimensionShutdownType'),
                        value: 'shutdownTypeName',
                        align: 'left',
                    });
                    break;
                case 'defect':
                    headers.push({ text: this.$t('tpmAnalytics.dimensionDefect'), value: 'defectDisplay', align: 'left' });
                    break;
            }
        }

        // Přidat standardní sloupce
        headers.push({ text: this.$t('tpmAnalytics.count'), value: 'count', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.duration'), value: 'durationHours', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.percentage'), value: 'percentage', align: 'left' });
        headers.push({ text: this.$t('tpmAnalytics.cumulativePercentage'), value: 'cumulativePercentage', align: 'left' });
        //headers.push({ text: 'ABC', value: 'abcCategory', align: 'center', width: '80px' });
        headers.push({ text: this.$t('tpmAnalytics.change'), value: 'changePct', align: 'left' });

        return headers;
    }

    get aggregatedDataSorted() {
        const items = [...this.aggregatedData];

        // Seřadit podle vybraného kritéria sestupně
        const sorted =
            this.sortBy === 'duration'
                ? items.sort((a, b) => (b.durationHours ?? 0) - (a.durationHours ?? 0))
                : items.sort((a, b) => (b.count ?? 0) - (a.count ?? 0));

        // Omezit na TOP N záznamů
        const limited = sorted.slice(0, this.aggregatedMaintenanceTop);

        // Vypočítat celkové hodnoty POUZE z omezených (TOP X) záznamů
        const totalCount = limited.reduce((sum, item) => sum + (item.count ?? 0), 0);
        const totalDuration = limited.reduce((sum, item) => sum + (item.durationHours ?? 0), 0);

        // Vypočítat kumulativní podíl a přiřadit ABC kategorii
        let cumulativeValue = 0;
        const itemsWithAnalysis = limited.map((item, index) => {
            const value = this.sortBy === 'duration' ? item.durationHours ?? 0 : item.count ?? 0;
            const total = this.sortBy === 'duration' ? totalDuration : totalCount;
            const percentage = total > 0 ? (value / total) * 100 : 0;
            cumulativeValue += value;
            const cumulativePercentage = total > 0 ? (cumulativeValue / total) * 100 : 0;

            // ABC kategorizace podle kumulativního podílu
            let abcCategory = 'C';
            let abcColor = '#4caf50'; // zelená pro C
            if (cumulativePercentage <= 80) {
                abcCategory = 'A';
                abcColor = '#f44336'; // červená pro A
            } else if (cumulativePercentage <= 95) {
                abcCategory = 'B';
                abcColor = '#ff9800'; // oranžová pro B
            }

            return {
                ...item,
                percentage,
                cumulativePercentage,
                abcCategory,
                abcColor,
                // Vytvořit kombinované zobrazení pro komponentu: "Název (Kód)"
                machineComponentDisplay:
                    item.machineComponentName && item.machineComponentName !== '--'
                        ? `${item.machineComponentName} (${item.machineComponentCode})`
                        : '--',
                // Vytvořit kombinované zobrazení pro defekt: "Název (Kód)"
                defectDisplay: item.defectName && item.defectName !== '--' ? `${item.defectName} (${item.defectCode})` : '--',
            };
        });

        return itemsWithAnalysis;
    }

    // Vytvořit label pro TreeMap buňku (přeskočit hodnoty "--")
    createTreemapLabel(item: any): string {
        const parts: string[] = [];
        for (const dim of this.selectedDimensions) {
            let value = '';
            switch (dim) {
                case 'location':
                    value = item.locationName || '';
                    break;
                case 'workplace':
                    value = item.workplaceName || '';
                    break;
                case 'station':
                    value = item.stationName || '';
                    break;
                case 'machineComponent':
                    value = item.machineComponentCode || '';
                    break;
                case 'shutdownType':
                    value = item.shutdownTypeName || '';
                    break;
                case 'defect':
                    value = item.defectCode || '';
                    break;
            }
            // Přidat pouze neprázdné hodnoty a ne "--"
            if (value && value !== '--') {
                parts.push(value);
            }
        }
        return parts.join(' / ');
    }

    get treemapSeries() {
        const data = this.aggregatedDataSorted.map((item) => ({
            x: this.createTreemapLabel(item),
            y: this.sortBy === 'duration' ? item.durationHours : item.count,
            fillColor: item.abcColor,
            meta: item, // Pro tooltip
        }));

        return [{ data }];
    }

    get treemapOptions() {
        return {
            chart: {
                type: 'treemap',
                toolbar: { show: false },
            },
            legend: { show: false },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    colors: ['#fff'],
                },
                formatter: (text: string, opt: any) => {
                    return text;
                },
            },
            plotOptions: {
                treemap: {
                    enableShades: false,
                    distributed: true,
                    colorScale: {
                        ranges: [],
                    },
                },
            },
            tooltip: {
                custom: ({ seriesIndex, dataPointIndex, w }: any) => {
                    const item = w.config.series[seriesIndex].data[dataPointIndex].meta;
                    const parts: string[] = [];

                    // Sestavit tooltip s kompletními informacemi (včetně "--")
                    for (const dim of this.selectedDimensions) {
                        switch (dim) {
                            case 'location':
                                parts.push(`<b>${this.$t('tpmAnalytics.dimensionLocation')}:</b> ${item.locationName || '--'}`);
                                break;
                            case 'workplace':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.dimensionWorkplace')}:</b> ${item.workplaceName || '--'}`
                                );
                                break;
                            case 'station':
                                parts.push(`<b>${this.$t('tpmAnalytics.dimensionStation')}:</b> ${item.stationName || '--'}`);
                                break;
                            case 'machineComponent':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.dimensionMachineComponent')}:</b> ${
                                        item.machineComponentDisplay || '--'
                                    }`
                                );
                                break;
                            case 'shutdownType':
                                parts.push(
                                    `<b>${this.$t('tpmAnalytics.dimensionShutdownType')}:</b> ${item.shutdownTypeName || '--'}`
                                );
                                break;
                            case 'defect':
                                parts.push(`<b>${this.$t('tpmAnalytics.dimensionDefect')}:</b> ${item.defectDisplay || '--'}`);
                                break;
                        }
                    }

                    parts.push(`<b>${this.$t('tpmAnalytics.count')}:</b> ${item.count}`);
                    parts.push(`<b>${this.$t('tpmAnalytics.duration')}:</b> ${this.formatHours(item.durationHours)}`);
                    parts.push(`<b>${this.$t('tpmAnalytics.percentage')}:</b> ${item.percentage.toFixed(1)}%`);

                    return `<div style="padding: 8px; background: white; border: 1px solid #e0e0e0; border-radius: 4px;">
                        ${parts.join('<br/>')}
                    </div>`;
                },
            },
        } as any;
    }

    dayName(di: number): string {
        const names = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return names[di] || '';
    }
    cellColor(v: number): string {
        const max = Math.max(1, ...this.heatGrid.reduce((a, r) => a.concat(r), [] as number[]));
        const ratio = v / max;
        const idx = v === 0 ? 0 : ratio >= 0.8 ? 4 : ratio >= 0.55 ? 3 : ratio >= 0.3 ? 2 : 1;
        return this.palette[idx];
    }
    get commonTime() {
        return `${this.timeFrom}:00 - ${this.timeTo}:00`;
    }
    get criticalDaysText() {
        return this.criticalDays.map((i) => this.dayName(i)).join(', ');
    }
    get failureTopHoursText() {
        return this.failureTopHours.map((h) => `${h}:00`).join(', ');
    }
    get failureCriticalDaysText() {
        return this.failureCriticalDays.map((i) => this.dayName(i)).join(', ');
    }
    get plannedStartsTopHoursText() {
        return this.plannedStartsTopHours.map((h) => `${h}:00`).join(', ');
    }
    get plannedStartsCriticalDaysText() {
        return this.plannedStartsCriticalDays.map((i) => this.dayName(i)).join(', ');
    }
    get plannedDurationCommonTime() {
        return `${this.plannedDurationTimeFrom}:00 - ${this.plannedDurationTimeTo}:00`;
    }
    get plannedDurationCriticalDaysText() {
        return this.plannedDurationCriticalDays.map((i) => this.dayName(i)).join(', ');
    }
    get mtbfMttrOptions() {
        return {
            chart: { id: 'mtbf-mttr', toolbar: { show: false } },
            plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
            xaxis: { categories: this.byWorkplace.map((x) => x.workplaceName) },
            yaxis: { labels: { formatter: (v: number) => v.toFixed(0) + 'h' } },
            colors: ['#22c55e', '#f59e0b'],
            dataLabels: { enabled: false },
            legend: { position: 'top' },
            grid: { show: false },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    shadeIntensity: 0.08,
                    opacityFrom: 0.9,
                    opacityTo: 0.82,
                    stops: [0, 80, 100],
                },
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => (isNaN(val as any) ? '-' : val.toFixed(1) + 'h'),
                },
            },
        } as any;
    }
    get mtbfMttrSeries() {
        return [
            { name: this.$t('tpmAnalytics.mtbf'), data: this.byWorkplace.map((x) => x.mtbfHours ?? 0) },
            { name: this.$t('tpmAnalytics.mttr'), data: this.byWorkplace.map((x) => x.mttrHours ?? 0) },
        ] as any;
    }

    get severityOptions() {
        return {
            chart: { id: 'severity', toolbar: { show: false } },
            labels: [...this.severityLabels],
            colors: [...this.severityColors],
            legend: { position: 'bottom' },
            dataLabels: { enabled: true },
        } as any;
    }
    get severitySeries() {
        return this.severityValues;
    }

    downloadChart(_id: string) {
        // placeholder for future export
    }
}
